<template>
    <div class="category-settings-tab tab-content">
        <div class="table-container-compact">
            <table class="table-compact">
                <thead>
                    <tr>
                        <th class="text-center">Scope</th>
                        <th class="text-center">카테고리 코드</th>
                        <th class="text-center">카테고리명</th>
                        <th class="text-center">비고</th>
                        <th class="text-center" v-if="canManageCategories"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td :colspan="canManageCategories ? 5 : 4" class="text-center">
                            <i class="fas fa-spinner fa-spin"></i> 로딩 중
                        </td>
                    </tr>
                    <tr v-else-if="categoryList.length === 0">
                        <td :colspan="canManageCategories ? 5 : 4" class="text-center empty-state">
                            <i class="fas fa-list-alt"></i>
                            <p>등록된 카테고리가 없습니다</p>
                        </td>
                    </tr>
                    <tr
                        v-else
                        v-for="category in paginatedCategories"
                        :key="category.categoryIdx || category.categoryCode"
                    >
                        <td class="text-center">{{ formatScopeType(category.scopeType) }}</td>
                        <td class="text-center"><strong>{{ category.categoryCode }}</strong></td>
                        <td class="text-center">{{ category.categoryName || "-" }}</td>
                        <td class="text-center">{{ category.remarks || "-" }}</td>
                        <td class="text-center" v-if="canManageCategories">
                            <div class="action-buttons text-center">
                                <IconButton
                                    icon="fas fa-edit"
                                    title="수정"
                                    @click.stop="openCategoryModal('edit', category)"
                                />
                                <IconButton
                                    icon="fas fa-trash"
                                    variant="delete"
                                    title="삭제"
                                    @click.stop="confirmDeleteCategory(category)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <TablePagination
            v-if="categoryList.length > 0"
            :total-items="categoryList.length"
            :current-page="categoryPagination.page"
            :page-size="categoryPagination.limit"
            @update:currentPage="onCategoryPageChange"
            @update:pageSize="onCategoryPageSizeChange"
        />

        <!-- 카테고리 추가/수정 모달 -->
        <b-modal
            v-model="showCategoryFormModal"
            :title="categoryFormMode === 'create' ? '카테고리 등록' : '카테고리 수정'"
            size="md"
            centered
        >
            <template #modal-header="{ close }">
                <div class="modal-form-header">
                    <h5 class="modal-title">
                        {{ categoryFormMode === "create" ? "카테고리 등록" : "카테고리 수정" }}
                    </h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <form @submit.prevent="saveCategory" class="modal-form-content">
                <div class="form-section">
                    <div class="section-header"><h6>기본 정보</h6></div>
                    <div class="form-group">
                        <label>Scope <span class="required">*</span></label>
                        <select v-model="categoryForm.scopeType" class="form-control" required>
                            <option value="">선택하세요</option>
                            <option v-for="opt in scopeTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>카테고리 코드 <span class="required">*</span></label>
                        <input
                            v-model="categoryForm.categoryCode"
                            type="text"
                            class="form-control"
                            maxlength="50"
                            required
                            placeholder="예: FIXED_COMBUSTION"
                            :readonly="categoryFormMode === 'edit'"
                        />
                        <small v-if="categoryFormMode === 'edit'" class="form-text text-muted"
                            >카테고리 코드는 수정할 수 없습니다.</small
                        >
                    </div>
                    <div class="form-group">
                        <label>카테고리명 <span class="required">*</span></label>
                        <input
                            v-model="categoryForm.categoryName"
                            type="text"
                            class="form-control"
                            maxlength="100"
                            required
                            placeholder="예: 고정 연소"
                        />
                    </div>
                    <div class="form-group">
                        <label>비고</label>
                        <textarea
                            v-model="categoryForm.remarks"
                            class="form-control"
                            rows="2"
                            maxlength="500"
                            placeholder="비고"
                        ></textarea>
                    </div>
                </div>
            </form>
            <template #modal-footer>
                <CarbonButton variant="secondary" @click="showCategoryFormModal = false">취소</CarbonButton>
                <CarbonButton variant="primary" @click="saveCategory">저장</CarbonButton>
            </template>
        </b-modal>
    </div>
</template>

<script>
import IconButton from "../../common/IconButton.vue";
import CarbonButton from "../../common/CarbonButton.vue";
import TablePagination from "../../common/TablePagination.vue";
import carbonApi from "../../../api";
import CarbonCodes from "../../../codes";

export default {
    name: "CategorySettingsTab",
    components: {
        IconButton,
        CarbonButton,
        TablePagination,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            loading: false,
            categoryList: [],
            categoryPagination: { page: 1, limit: 10 },
            showCategoryFormModal: false,
            categoryFormMode: "create",
            categoryForm: this.getEmptyCategoryForm(),
        };
    },
    computed: {
        paginatedCategories() {
            const start = (this.categoryPagination.page - 1) * this.categoryPagination.limit;
            const end = start + this.categoryPagination.limit;
            return this.categoryList.slice(start, end);
        },
        scopeTypeOptions() {
            return CarbonCodes.scopeType.getList();
        },
        canManageCategories() {
            const masterDataPerms =
                this.loginUser?.carbonPermissions?.masterData || this.loginUser?.masterDataPermissions;
            if (!masterDataPerms) return true;

            let actions = [];
            if (Array.isArray(masterDataPerms)) {
                actions = masterDataPerms;
            } else if (Array.isArray(masterDataPerms.actions)) {
                actions = masterDataPerms.actions;
            } else if (typeof masterDataPerms === "object") {
                actions = Object.keys(masterDataPerms).filter((k) => masterDataPerms[k]);
            }
            const lowered = actions.map((a) => String(a).toLowerCase());
            return lowered.some((a) => ["write", "delete", "admin"].includes(a));
        },
    },
    mounted() {
        this.loadCategories();
    },
    methods: {
        getEmptyCategoryForm() {
            return {
                categoryIdx: null,
                scopeType: "SCOPE1",
                categoryCode: "",
                categoryName: "",
                remarks: "",
            };
        },
        formatScopeType(scopeType) {
            return CarbonCodes.scopeType.getLabel(scopeType) || scopeType || "-";
        },
        onCategoryPageChange(page) {
            this.categoryPagination = { ...this.categoryPagination, page };
        },
        onCategoryPageSizeChange(size) {
            this.categoryPagination = { page: 1, limit: size };
        },
        async loadCategories() {
            if (!this.canManageCategories && this.categoryList.length > 0) return;
            this.loading = true;
            try {
                const res = await carbonApi.category.getCategories();
                const raw = res.data?.data ?? res.data;
                const list = raw?.categories ?? raw ?? [];
                this.categoryList = Array.isArray(list) ? list : [];
            } catch (err) {
                console.error("[CategorySettingsTab] 카테고리 목록 조회 실패:", err);
                this.$bvToast?.toast(err.response?.data?.message || "카테고리 목록을 불러오지 못했습니다.", {
                    title: "오류",
                    variant: "danger",
                    autoHideDelay: 5000,
                });
                this.categoryList = [];
            } finally {
                this.loading = false;
            }
        },
        openCategoryModal(mode, category = null) {
            if (!this.canManageCategories) {
                this.$bvToast?.toast("카테고리를 수정할 권한이 없습니다.", {
                    title: "권한 없음",
                    variant: "warning",
                    autoHideDelay: 4000,
                });
                return;
            }
            this.categoryFormMode = mode;
            if (mode === "create") {
                this.categoryForm = { ...this.getEmptyCategoryForm() };
            } else if (category) {
                this.categoryForm = {
                    categoryIdx: category.categoryIdx ?? null,
                    scopeType: category.scopeType || "SCOPE1",
                    categoryCode: category.categoryCode || "",
                    categoryName: category.categoryName || "",
                    remarks: category.remarks || "",
                };
            }
            this.showCategoryFormModal = true;
        },
        async saveCategory() {
            if (!this.canManageCategories) return;
            const form = this.categoryForm;
            if (!form.scopeType || !form.categoryCode || !form.categoryName) {
                this.$bvToast?.toast("Scope, 카테고리 코드, 카테고리명을 입력하세요.", { variant: "warning" });
                return;
            }
            const payload = {
                scopeType: form.scopeType,
                categoryCode: form.categoryCode.trim(),
                categoryName: form.categoryName.trim(),
                remarks: form.remarks?.trim() || null,
            };
            try {
                if (this.categoryFormMode === "create") {
                    await carbonApi.category.createCategory(payload);
                    this.$bvToast?.toast("카테고리가 등록되었습니다.", { variant: "success" });
                } else if (form.categoryIdx != null) {
                    await carbonApi.category.updateCategory(form.categoryIdx, payload);
                    this.$bvToast?.toast("카테고리가 수정되었습니다.", { variant: "success" });
                }
                this.showCategoryFormModal = false;
                await this.loadCategories();
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "카테고리 저장에 실패했습니다.";
                this.$bvToast?.toast(msg, { title: "오류", variant: "danger", autoHideDelay: 5000 });
            }
        },
        async confirmDeleteCategory(category) {
            if (!this.canManageCategories) return;
            if (!category || category.categoryIdx == null) return;

            const code = category.categoryCode;
            const name = category.categoryName || "";
            const confirm = await this.alertConfirmWarning(
                name
                    ? `카테고리 '${code} / ${name}'을(를) 삭제하시겠습니까?\n참조 중인 데이터가 있으면 삭제할 수 없습니다.`
                    : `카테고리 '${code}'을(를) 삭제하시겠습니까?\n참조 중인 데이터가 있으면 삭제할 수 없습니다.`,
            );
            if (!confirm.value) return;

            try {
                await carbonApi.category.deleteCategory(category.categoryIdx);
                this.$bvToast?.toast("카테고리가 삭제되었습니다.", { variant: "success" });
                await this.loadCategories();
            } catch (err) {
                const status = err.response?.status;
                const baseMsg =
                    err.response?.data?.message ||
                    (status === 409
                        ? "카테고리가 다른 데이터에서 참조 중이라 삭제할 수 없습니다."
                        : err.message || "카테고리 삭제에 실패했습니다.");
                this.$bvToast?.toast(baseMsg, { title: "오류", variant: "danger", autoHideDelay: 5000 });
            }
        },
    },
};
</script>

<style scoped>
.category-settings-tab {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.modal-form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.form-section {
    margin-bottom: 16px;
}

.section-header {
    margin-bottom: 8px;
}

.section-header h6 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.form-group {
    margin-bottom: 12px;
}

.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 500;
}

.form-group .form-control {
    width: 100%;
    padding: 6px 10px;
    font-size: 13px;
    border: 1px solid #ced4da;
    border-radius: 4px;
}

.required {
    color: #dc3545;
}
</style>
